/**
 * FeatureFlagsConfig - Super-admin feature flag management
 *
 * Lists all PLATFORM flags grouped by category with toggle switches.
 * Route: /admin/platform/feature-flags
 * Access: AAIM_SUPER_ADMIN only
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/generated/platform-admin';
import { Heading, BodyText, Card } from '@aaimplatform/ui';
import { Loader2 } from 'lucide-react';
import { platformAdminClient } from '../../api/typed-clients';

type FeatureFlag = components['schemas']['FeatureFlag'];

const CATEGORY_LABELS: Record<string, string> = {
  'asset-class': 'Asset Classes',
  'monitoring': 'Monitoring Channels',
  'advanced': 'Advanced Features',
};

export function FeatureFlagsConfigPage() {
  const queryClient = useQueryClient();

  const flagsQuery = useQuery({
    queryKey: ['platform-feature-flags'],
    queryFn: async (): Promise<FeatureFlag[]> => {
      const { data, error } = await platformAdminClient.GET('/settings/feature-flags');
      if (error) throw new Error('Failed to fetch feature flags');
      return (data as FeatureFlag[]) ?? [];
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) => {
      const { error } = await platformAdminClient.PATCH('/settings/feature-flags', {
        body: { [key]: enabled },
      });
      if (error) throw new Error('Failed to update flag');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-feature-flags'] });
    },
  });

  const flags = flagsQuery.data ?? [];

  // Group by category
  const grouped = flags.reduce<Record<string, FeatureFlag[]>>((acc, flag) => {
    const cat = flag.category ?? 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(flag);
    return acc;
  }, {});

  if (flagsQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-secondary">
        <Loader2 className="w-4 h-4 animate-spin" />
        <BodyText size="sm">Loading feature flags...</BodyText>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Heading level={2} size="section">Feature Flags</Heading>
        <BodyText size="sm" color="secondary" className="mt-1">
          Platform-level feature flags control availability across all tenants.
          Disabled flags cannot be enabled by individual tenants.
        </BodyText>
      </div>

      {Object.entries(grouped).map(([category, categoryFlags]) => (
        <Card key={category} className="p-6">
          <Heading level={3} size="label" className="mb-4">
            {CATEGORY_LABELS[category] ?? category}
          </Heading>
          <div className="space-y-3">
            {categoryFlags.map((flag) => (
              <div key={flag.key} className="flex items-center justify-between py-2 border-b border-recessed last:border-b-0">
                <div>
                  <BodyText size="sm" className="font-medium">
                    {flag.name ?? flag.key}
                  </BodyText>
                  {flag.description && (
                    <BodyText size="sm" color="secondary">{flag.description}</BodyText>
                  )}
                  <BodyText size="sm" color="tertiary" className="font-mono text-xs mt-0.5">
                    {flag.key}
                  </BodyText>
                </div>
                <button
                  role="switch"
                  aria-checked={flag.enabled}
                  onClick={() => toggleMutation.mutate({ key: flag.key, enabled: !flag.enabled })}
                  disabled={toggleMutation.isPending}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    flag.enabled ? 'bg-accent' : 'bg-recessed'
                  } ${toggleMutation.isPending ? 'opacity-50' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-surface transition-transform ${
                      flag.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </Card>
      ))}

      {flags.length === 0 && (
        <Card className="p-8 text-center">
          <BodyText size="sm" color="secondary">
            No feature flags configured. Flags are seeded on server startup.
          </BodyText>
        </Card>
      )}
    </div>
  );
}
